import Component from 'vue-class-component';
import { Mixins, Prop } from 'vue-property-decorator';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { required, maxLength, decimal, numeric, between  } from 'vuelidate/lib/validators';
import { IPaperSelected } from '@/templates/interfaces/paper-selected.interface';
import { PAPER_SELECTED_TYPES, _TEMPLATES_STORE } from '@/templates/values/templates.values';
import { IParamsCreateWriteObject } from '@/courses/interfaces/params-create-write-object.interface';
import { IParamsPaperWriteObject } from '@/templates/interfaces/params-paper-write-object.interface';
import EdPapersWriteObject from '@/templates/components/ed-papers-write-object/ed-papers-write-object.vue';
import { CreateSplitObjectMixin } from '@/courses/mixins/create-split-object.mixin';
import { COURSE_MATERIAL_KEYS, FILE_TO_WRITE_OBJECT } from '@/courses/values/courses.values';
import SectionSchoolCalendarMixin from '@/sections/mixins/section-school-calendar.mixin';
import { SectionProvider } from '@/sections/providers/section.provider';
import { Section } from '@/courses/models/section.model';
import { SectionSchooolCalendarProvider } from '@/sections/providers/section-school-calendar.provider';
import KEYS from '@/values/keys';


@Component({
  components: {
    EdPapersWriteObject,
    EdSelectTag: () => import(/* EdSelectTag */'@/courses/components/ed-select-tag/ed-select-tag.component.vue')
  }
})
export default class EdCreateWriteObject extends Mixins(SectionSchoolCalendarMixin, CreateSplitObjectMixin) {

  // @Prop() readonly params: IParamsCreateWriteObject;   //Typescript upgrade
  declare readonly params: IParamsCreateWriteObject; 
  @Prop() readonly saveObject: any;
  @Prop() readonly hasGrading: boolean;
  @Prop() readonly section: Section;
  @Prop() readonly product: any;

  public form: any = EdFormModel([
    EdInputModel('name', '', { required }),
  ]);
  public hasPaperSelected: boolean = false;
  public paperUploaded: IPaperSelected = {
    paperId: 0,
    type: PAPER_SELECTED_TYPES.NONE,
    coverFile: null,
    paperFile: null
  };

  public file: any = null; 
  public isSaving: boolean = false;
  public paramsPaperSelect: IParamsPaperWriteObject = { 
    edition: false,
    type: PAPER_SELECTED_TYPES.UNDEF 
  };

  public edition = {
    status: false,
    deleteQuestion: false,
    deleteSolution: false
  };

  public isMobile: boolean = false;
  public dropdownMenus: { problem: 'problem', response: 'response', solution: 'solution' };
  public dropdownOpened: string = 'problem';
  public materialSelected: boolean = false;
  public currentProblem: any = {};
  public tags: Array<string> = ['TEST', 'CLASSWORK', 'HOMEWORK'];
  public tagSelected: string = null;
  public showListComponent: boolean = true;


  get title() {
    return this.edition.status ? this.$i18n.t('courses.writeObject.edit') : this.$i18n.t('courses.writeObject.create');
  }

  get hasFiles() {
    return this.hasPaperSelected && this.hasProblemFile;
  }

  get canSave() {
    return this.hasFiles && this.problemFile.valid && this.form.valid && this.isSolutionValid;
  }

  get shouldArchiveCurrentHomeworks(){
    if(this.params.paperSelected.type != this.paperUploaded.type ||
      this.params.paperSelected.paperId != this.paperUploaded.paperId){
      return true;
    }
    if(this.params.problem.type != this.problemFile.type){
      return true;
    }
    if(this.params.problem.type == FILE_TO_WRITE_OBJECT.COURSE_MATERIAL &&
      ( this.params.problem.data.sectionMaterial.materialId != this.problemFile.data.material.relatedContentId ||
        this.params.problem.data.sectionMaterial.startSection != this.problemFile.data.start ||
        this.params.problem.data.sectionMaterial.endSection != this.problemFile.data.end)){
      return true;
    }
    if(this.params.problem.type == FILE_TO_WRITE_OBJECT.FILE && this.problemFile.data instanceof File){
      return true;
    }
    return false;
  }

  get hasBeenResolvedByAnyStudent(){
    return this.params.hasBeenResolvedByAnyStudent ? this.params.hasBeenResolvedByAnyStudent : false;
  }

  get eduacPapers(){
    return this.$store.state.TemplatesStore.eduacPapers.content;
  }

  get courseType(): string {
    return this.$store.state.CourseStore.courses.content.find(
      (course: any) => course.id === this.params.courseId
    )?.type;
  }

  get showDisclaimer(): boolean {
    return this.params.edition && ![KEYS.CONTENT_TYPE.COURSE_TEMPLATE, KEYS.CONTENT_TYPE.CST].includes(this.courseType);
  }

  get components() {
    return this.product.eduacComponents;
  }

  init(name: string = '', dueDate: string = '', lastDate: string = '', homeworkPoints: number = 100, homeworkWeight: number = 1) {
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(60) }),
      EdInputModel('dueDate', dueDate, { }),
      EdInputModel('lastDate', lastDate, { }),
      EdInputModel('homeworkPoints', homeworkPoints, { numeric, between: between(1, 1000)}, true),
      EdInputModel('homeworkWeight', homeworkWeight, { numeric, between: between(1, 1000)}, true)
    ]);
    if(this.hasGrading){
      this.form.setValidator('homeworkPoints', 'required', required);
      this.form.setValidator('homeworkWeight', 'required', required);
    }
  }

  onProblemChanged(result: { data: any, valid: boolean, type: string }) {
    this.problemFile = result;
    /* Set name of object for default */
    if(this.problemFile.data && !this.form.name.value)  {
      this.form.set('name', this.problemFile.data.name.split('.')[0]);
      this.form.setValid('name', true);
    }
    /* Check if the problem created previosly document was removed */
    if(this.edition.status) {
      this.edition.deleteQuestion = this.getIsTheSameCourseMaterial(this.params.problem, this.problemFile);
    }

    this.checkComponentSelected(this.problemFile);
  }

  onSolutionChanged(result: { data: any, valid: boolean, type: string }) {
    this.solutionFile = result;
    /* Check if the solution created previosly document was removed */
    if(this.edition.status) {
      this.edition.deleteSolution = this.getIsTheSameCourseMaterial(this.params.solution, this.solutionFile);
    }
  }

  onAnswerAdded(paperSelected: IPaperSelected) {
    this.paperUploaded = paperSelected;
    this.hasPaperSelected = paperSelected.type !== PAPER_SELECTED_TYPES.UNDEF;    
  }

  save() {
    if(this.edition.status && this.params.resolved && this.shouldArchiveCurrentHomeworks) {
      this.$EdModal.createConfirmationModal(
        {
          body: this.$i18n.t('courses.writeObject.confirmEdit'), 
          action: () => {
            this.saveWriteObject();
          }
        }
      );
    } else {
      this.saveWriteObject();
    }
    this.$store.commit(_TEMPLATES_STORE.MUTATIONS.CLEAR_PAPER_SELECTED);
  }

  async saveWriteObject() {
    this.isSaving = true;
    const dueDateInfo = SectionProvider.getDueDateInfo(
      this.form,
      this.defaultDueDate,
      this.defaultLastDate,
      this.params.edition,
      this.section,
      this.params.submissionDates
    );
    const paperSelected = await this.getPaperSelected();
    let payload: any = {
      edition: this.edition,
      question: this.problemFile,
      solution: this.solutionFile,
      name: this.form.name.value,
      answer: {
        paperId: paperSelected.paperId,
        image: paperSelected.coverFile,
        paper: paperSelected.paperFile,
        type: paperSelected.type
      },
      dueDate: this.form.dueDate.value,
      lastDate: this.form.lastDate.value,
      ...dueDateInfo,
      homeworkPoints: this.form.homeworkPoints.value,
      homeworkWeight: this.form.homeworkWeight.value,
      labelSections: this.tagSelected,
      component: this.componentSelected
    };
    this.$emit('saveObject', payload);
  }

  async getPaperSelected():Promise<any> {
    if( this.problemFile.data.isMultimediaFile && !this.paperUploaded.paperId && !this.paperUploaded.paperFile) {
      let blankPageTemplateId = await this.getBlankPageTemplateId();
      return {
        paperId: blankPageTemplateId,
        type: PAPER_SELECTED_TYPES.EDUAC_PAPER,
      };
    } else {
      return this.paperUploaded;
    }
  }

  async getBlankPageTemplateId():Promise<any> {
    if( !this.eduacPapers.length ) {
      await this.$store.dispatch(_TEMPLATES_STORE.ACTIONS.GET_EDUAC_PAPERS);
    }
    let paperBlankPage = this.eduacPapers[0];
    return paperBlankPage?.templateId;
  }

  createSuccess() {
    this.isSaving = false;
    if(this.paperUploaded.type == PAPER_SELECTED_TYPES.UPLOAD) {
      this.$store.commit(_TEMPLATES_STORE.MUTATIONS.RESET_USER_PAPERS_LOAD);
    }
    this.closeModal();
  }

  createFailed() {
    this.isSaving = false;
  }

  closeModal() {
    this.$store.commit(_TEMPLATES_STORE.MUTATIONS.CLEAR_PAPER_SELECTED);
    this.$EdModal.closeModal();
  }


  /* Edition */
  setDataForEdition() {
    this.edition.status = true;
    this.setProblemForEdition(this.params.problem, this.params.courseId);
    this.setSolutionForEdition(this.params.solution, this.params.courseId);
    this.paramsPaperSelect = {
      edition: true,
      type: this.params.paperSelected.type,
      paperId: this.params.paperSelected.paperId,
      name: this.section.template?.name,
      coverFile: this.section.template?.assetImageInfo.url,
      paperFile: this.section.template?.assetDataInfo.url,
    };
    this.onAnswerAdded(this.params.paperSelected);
    this.init(
      this.params.name,
      SectionSchooolCalendarProvider.getSchoolDayDate(this.params.submissionDates.dueSchoolDay),
      SectionSchooolCalendarProvider.getSchoolDayDate(this.params.submissionDates.lastSchoolDay),
      this.params.homeworkPoints,
      this.params.homeworkWeight
    );

    this.hasBeenHomeworkResolvedByAnyStudent = this.hasBeenResolvedByAnyStudent;
    this.tagSelected = this.params.labelSections;
    this.checkComponentSelected(this.params.problem);
  }

  checkComponentSelected(problemData) {
    if(this.params.hasTagSelector ) {
      if( problemData.type === 'COURSE_MATERIAL' && problemData.data ) {
        this.componentSelected = this.components.find(c => c.id == problemData.data.component?.id) ?? this.components.find(c => c.mandatory);
        this.showListComponent = false; 
      } else {
        this.componentSelected = this.componentSelected ? this.componentSelected : this.components.find(c => c.id == this.params.component?.id) ?? this.components.find(c => c.mandatory);
        this.showListComponent = true;
      }
    }
  }

  beforeCreate() {
    this.componentSelected = null;
  }

  /* Hooks */
  created() {
    if(this.params.edition) {
      this.setDataForEdition();
    }
  }

  mounted() {
    if(!this.params.edition) {
      let dueDate = this.hasSchoolDays ? this.defaultDueDate : '';
      let lastDate = this.hasSchoolDays ? this.defaultLastDate : '';
      this.init('', dueDate, lastDate);
      if(this.product.eduacComponents) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
    }
  }

}